"use client";

// next
import Image from "next/image";
import React from "react";

// components
import { Button } from "@/components/ui/Button";

// i18n
import { Link, useRouter } from "@/i18n/navigation";
import { useLocale } from "next-intl";

// hooks
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useCurrency } from "@/contexts/CurrencyContext";

// utils
import clsx from "clsx";
import { formatDate, getRelativeDateLabel } from "@/utils/dateFormatter";
import { formatBusPrice } from "@/utils/bus/priceUtils";

function pushSelectRecommendationDataLayer(recommendationPayload) {
  if (typeof window === "undefined" || !recommendationPayload) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...recommendationPayload,
    event_action: "select_recommendation",
  });
}

function RouteDealCard(props) {
  const t = useTranslationDetector("bus");
  const router = useRouter();
  const locale = useLocale();

  const raw = props.item || {};
  const redirectUrl = raw.RedirectUrl || "/";
  const dateFormatType = props.dateFormatType || "relative"; // relative or standard
  const useButtonNavigation = props.useButtonNavigation !== false; // true: button onClick (no href), false: Link with href (SEO)

  // Yeni API datasını eski RouteCard prop'larına map et
  const date = dateFormatType === "relative" ? t(getRelativeDateLabel(raw.Date)) : formatDate(raw.Date, { locale, format: "DD MMM ddd" });
  const logo = raw.CompanyImageUrl || "";
  const fromCity = raw.FromCity || "";
  const fromStation = raw.FromStation || "";
  const fromTime = raw.FromTime || "";
  const duration = raw.Duration || "";
  const toCity = raw.ToCity || "";
  const toStation = raw.ToStation || "";
  const toTime = raw.ToTime || "";

  const { currency } = useCurrency();
  const price = formatBusPrice(raw.Price, currency);

  const handleClick = () => {
    if (props.onAnalyticsClick) {
      props.onAnalyticsClick(props.item);
    } else if (props.recommendationPayload) {
      pushSelectRecommendationDataLayer(props.recommendationPayload);
    }
  };

  return (
    <span className="flex w-full flex-col gap-4 rounded-lg border border-primary-weak bg-white px-6 py-5  text-left"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <figure>
            <Image 
              src={logo} 
              width={137} 
              height={40} 
              alt="logo"
              loading="lazy" 
              className="object-contain max-h-[40px] object-left" 
            />
          </figure>
        </div>
        <span className="text-sm leading-[19px] text-foreground-default">{date}</span>
      </div>
      <div>
        <div className="flex w-full flex-col gap-1">
          <div className="relative grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 overflow-hidden">
            <div className="text-foreground-primary z-10 justify-self-start bg-white text-lg font-semibold">
              <p
                className={clsx(
                  "overflow-hidden text-lg leading-[26px] font-semibold text-ellipsis",
                  "[@media(min-width:400px)]:w-[85px]",
                  "[@media(min-width:720px)]:w-[85px]",
                  "lg:w-[180px]",
                )}
              >
                {fromCity}
              </p>
            </div>
            <span
              className={clsx(
                "relative mx-2 flex h-[26px] items-center rounded-sm bg-gray-100 px-[8px] text-center text-xs leading-[26px] whitespace-nowrap",
                // before pseudo-element
                "before:border-border-card before:absolute before:top-1/2 before:right-full before:mr-2 before:w-[50px] before:-translate-y-1/2 before:border-t before:content-['']",
                // after pseudo-element
                "after:border-border-card after:absolute after:top-1/2 after:left-full after:ml-2 after:w-[50px] after:-translate-y-1/2 after:border-t after:content-['']",
              )}
            >
              {duration}
            </span>
            <div className="text-foreground-primary z-10 justify-self-end bg-white text-lg font-semibold">
              <p
                className={clsx(
                  "overflow-hidden text-right text-lg font-semibold text-ellipsis",
                  "[@media(min-width:400px)]:w-[85px]",
                  "[@media(min-width:720px)]:w-[85px]",
                  "lg:w-[180px]",
                )}
              >
                {toCity}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-foreground-muted">{fromStation}</p>
            <p className="mt-1 font-medium">{fromTime}</p>
          </div>

          <div className="text-right">
            <p className="text-foreground-muted">{toStation}</p>
            <p className="mt-1 font-medium">{toTime}</p>
          </div>
        </div>
      </div>
      <div className="border-border-card w-full border-t"></div>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-xl leading-26 text-foreground-default">
          {price}
        </p>
        {useButtonNavigation ? (
          <Button
            className="flex h-[36px] flex-row items-center px-4 lg:px-4"
            onClick={() => {handleClick(); window.location.href = redirectUrl;}}
          >
            <span className="text-sm">{t("btnRouteDealBuyNow")}</span>
          </Button>
        ) : (
          <Link href={redirectUrl} className="flex" onClick={() => {handleClick();}}>
            <Button className="flex h-[36px] flex-row items-center px-4 lg:px-4">
              <span className="text-sm">{t("btnRouteDealBuyNow")}</span>
            </Button>
          </Link>
        )}
      </div>
    </span>
  );
}

export default RouteDealCard;